/* ============================================
   NOVEL READER COMPONENT
   The Creative Orchestra - GEMINI Framework
   Main novel reading interface
   ============================================ */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { List, Settings, Sun, Moon } from 'lucide-react';
import ChapterContent, { ChapterSkeleton } from './ChapterContent';
import ChapterNavigation, { FloatingChapterNav } from './ChapterNavigation';
import TableOfContents from './TableOfContents';
import ReadingProgressBar, { MiniProgressBar } from './ReadingProgress';
import BookmarkButton, { BookmarkIndicator } from './BookmarkButton';
import { ScrollToTopButton } from '@components/common/ScrollToTop';
import { getChapterById, chapterList } from '@data';
import useBookmark from '@hooks/useBookmark';
import useReadingProgress from '@hooks/useReadingProgress';

/**
 * Novel Reader Component
 * Main reading interface with:
 * - Chapter content rendering
 * - Navigation (prev/next)
 * - Table of contents
 * - Reading progress tracking
 * - Bookmark functionality
 * - Reading settings
 */
const NovelReader = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [chapterContent, setChapterContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTocOpen, setIsTocOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [readerSettings, setReaderSettings] = useState({
    fontSize: 'normal', // 'small' | 'normal' | 'large'
    theme: 'dark', // 'dark' | 'sepia'
    width: 'normal', // 'narrow' | 'normal' | 'wide'
  });

  // Hooks
  const { markChapterVisited, bookmark, saveBookmark } = useBookmark();
  const { progress, scrollPosition, scrollToProgress } = useReadingProgress();

  // Get current chapter data
  const currentChapter = useMemo(() => {
    const id = chapterId || '01';
    return getChapterById(id);
  }, [chapterId]);

  // Redirect to first chapter if no ID
  useEffect(() => {
    if (!chapterId) {
      navigate('/novel/01', { replace: true });
    }
  }, [chapterId, navigate]);

  // Load chapter content
  useEffect(() => {
    const loadChapter = async () => {
      if (!currentChapter) return;

      setIsLoading(true);

      try {
        // Dynamic import of markdown file using Vite's raw import
        // The ?raw query tells Vite to import the file as a raw string
        const modules = import.meta.glob('@data/story/*.md', { 
          eager: false, 
          query: '?raw',
          import: 'default' 
        });
        
        // Find the matching module
        const moduleKey = Object.keys(modules).find(key => key.endsWith(currentChapter.file));
        
        if (moduleKey) {
          const content = await modules[moduleKey]();
          setChapterContent(content);
        } else {
          throw new Error(`Chapter file not found: ${currentChapter.file}`);
        }
        
        // Mark chapter as visited
        markChapterVisited(currentChapter.id);
      } catch (error) {
        console.error('Failed to load chapter:', error);
        setChapterContent('# Chapter not found\n\nThis chapter content is not yet available.');
      } finally {
        setIsLoading(false);
      }
    };

    loadChapter();
  }, [currentChapter, markChapterVisited]);

  // Restore scroll position if coming from bookmark (disabled for better UX)
  // useEffect(() => {
  //   if (!isLoading && bookmark?.chapterId === chapterId && bookmark?.scrollPosition) {
  //     // Small delay to ensure content is rendered
  //     setTimeout(() => {
  //       scrollToProgress(bookmark.scrollPercentage, false);
  //     }, 100);
  //   }
  // }, [isLoading, bookmark, chapterId, scrollToProgress]);

  // Auto-save bookmark periodically
  useEffect(() => {
    if (isLoading || !currentChapter) return;

    const autoSaveInterval = setInterval(() => {
      if (progress > 5) { // Only save if scrolled past 5%
        saveBookmark(currentChapter.id, {
          scrollPosition,
          scrollPercentage: progress,
          chapterTitle: currentChapter.title,
        });
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [isLoading, currentChapter, progress, scrollPosition, saveBookmark]);

  // Reader width classes
  const widthClasses = {
    narrow: 'max-w-xl',
    normal: 'max-w-3xl',
    wide: 'max-w-5xl',
  };

  // Font size classes
  const fontSizeClasses = {
    small: 'text-sm md:text-base',
    normal: 'text-base md:text-lg',
    large: 'text-lg md:text-xl',
  };

  // Theme classes
  const themeClasses = {
    dark: 'bg-transparent',
    sepia: 'bg-amber-950/10',
  };

  if (!currentChapter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Chapter not found</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses[readerSettings.theme]}`}>
      {/* Progress bar */}
      <ReadingProgressBar />

      {/* Floating navigation */}
      <FloatingChapterNav currentChapterId={currentChapter.id} />

      {/* Main content */}
      <div className={`mx-auto px-4 py-8 md:py-16 ${widthClasses[readerSettings.width]}`}>
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10">
          {/* TOC toggle */}
          <motion.button
            onClick={() => setIsTocOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg
              bg-white/5 text-gray-400 hover:text-white
              hover:bg-white/10 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <List className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Contents</span>
          </motion.button>

          {/* Center - mini progress */}
          <div className="flex-grow mx-4 max-w-xs hidden sm:block">
            <MiniProgressBar />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <BookmarkButton
              chapterId={currentChapter.id}
              chapterTitle={currentChapter.title}
              variant="mini"
            />
            <motion.button
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
              className="p-2 rounded-lg text-gray-400 hover:text-white
                hover:bg-white/10 transition-colors"
              whileTap={{ scale: 0.9 }}
            >
              <Settings className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Settings panel */}
        <AnimatePresence>
          {isSettingsOpen && (
            <ReaderSettings
              settings={readerSettings}
              onChange={setReaderSettings}
              onClose={() => setIsSettingsOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Bookmark indicator */}
        <BookmarkIndicator 
          chapterId={currentChapter.id} 
          className="mb-4"
        />

        {/* Chapter content */}
        <div className={fontSizeClasses[readerSettings.fontSize]}>
          {isLoading ? (
            <ChapterSkeleton />
          ) : (
            <ChapterContent
              content={chapterContent}
              chapter={currentChapter}
            />
          )}
        </div>

        {/* Bottom navigation */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <ChapterNavigation
            currentChapterId={currentChapter.id}
            onOpenToc={() => setIsTocOpen(true)}
          />
        </div>

        {/* Floating bookmark button */}
        <BookmarkButton
          chapterId={currentChapter.id}
          chapterTitle={currentChapter.title}
          variant="floating"
        />
      </div>

      {/* Table of Contents Modal */}
      <TableOfContents
        currentChapterId={currentChapter.id}
        isOpen={isTocOpen}
        onClose={() => setIsTocOpen(false)}
        variant="modal"
      />

      {/* Scroll to Top Button */}
      <ScrollToTopButton threshold={400} />
    </div>
  );
};

/**
 * Reader Settings Panel
 */
const ReaderSettings = ({ settings, onChange, onClose }) => {
  return (
    <motion.div
      className="mb-8 p-4 rounded-xl bg-white/5 border border-white/10"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">Reading Settings</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Font size */}
        <div>
          <label className="text-xs text-gray-500 mb-2 block">Font Size</label>
          <div className="flex rounded-lg bg-black/20 p-1">
            {['small', 'normal', 'large'].map((size) => (
              <button
                key={size}
                onClick={() => onChange({ ...settings, fontSize: size })}
                className={`
                  flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors
                  ${settings.fontSize === size
                    ? 'bg-white/10 text-white'
                    : 'text-gray-500 hover:text-white'
                  }
                `}
              >
                {size === 'small' ? 'A' : size === 'normal' ? 'A' : 'A'}
                <span className="ml-1 capitalize">{size}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Width */}
        <div>
          <label className="text-xs text-gray-500 mb-2 block">Width</label>
          <div className="flex rounded-lg bg-black/20 p-1">
            {['narrow', 'normal', 'wide'].map((width) => (
              <button
                key={width}
                onClick={() => onChange({ ...settings, width })}
                className={`
                  flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors capitalize
                  ${settings.width === width
                    ? 'bg-white/10 text-white'
                    : 'text-gray-500 hover:text-white'
                  }
                `}
              >
                {width}
              </button>
            ))}
          </div>
        </div>

        {/* Theme */}
        <div>
          <label className="text-xs text-gray-500 mb-2 block">Theme</label>
          <div className="flex rounded-lg bg-black/20 p-1">
            <button
              onClick={() => onChange({ ...settings, theme: 'dark' })}
              className={`
                flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors
                flex items-center justify-center gap-1
                ${settings.theme === 'dark'
                  ? 'bg-white/10 text-white'
                  : 'text-gray-500 hover:text-white'
                }
              `}
            >
              <Moon className="w-3 h-3" />
              Dark
            </button>
            <button
              onClick={() => onChange({ ...settings, theme: 'sepia' })}
              className={`
                flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors
                flex items-center justify-center gap-1
                ${settings.theme === 'sepia'
                  ? 'bg-white/10 text-white'
                  : 'text-gray-500 hover:text-white'
                }
              `}
            >
              <Sun className="w-3 h-3" />
              Sepia
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NovelReader;
